import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">Reach Out</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="contact-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            Contact
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      {/* Contact Form */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h2 className="font-heading text-2xl sm:text-3xl uppercase mb-6">Send a Message</h2>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-heading text-sm uppercase block mb-2">Name *</label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Your name"
                    className="rounded-none border-none bg-muted font-body"
                  />
                </div>
                <div>
                  <label className="font-heading text-sm uppercase block mb-2">Email *</label>
                  <Input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="rounded-none border-none bg-muted font-body"
                  />
                </div>
              </div>
              <div>
                <label className="font-heading text-sm uppercase block mb-2">Subject</label>
                <Input
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="What's this about?"
                  className="rounded-none border-none bg-muted font-body"
                />
              </div>
              <div>
                <label className="font-heading text-sm uppercase block mb-2">Message *</label>
                <Textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Your message..."
                  rows={6}
                  className="rounded-none border-none bg-muted font-body"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase px-8 py-4 text-lg border-none h-auto"
              >
                <Send size={16} className="mr-2" />
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>

          <div className="md:col-span-5">
            <h2 className="font-heading text-2xl sm:text-3xl uppercase mb-6">The Territory Office</h2>
            <div className="bg-foreground text-background p-8 sm:p-10">
              <div className="space-y-6 font-body text-sm">
                <div>
                  <p className="text-background/50 uppercase tracking-widest text-xs mb-1">Email</p>
                  <a href="mailto:Piedmontwrestlingclub@Gmail.com" className="text-background hover:text-primary transition-colors">Piedmontwrestlingclub@Gmail.com</a>
                </div>
                <div>
                  <p className="text-background/50 uppercase tracking-widest text-xs mb-1">Region</p>
                  <p className="text-background">The Piedmont Territory</p>
                </div>
                <div>
                  <p className="text-background/50 uppercase tracking-widest text-xs mb-1">Inquiries</p>
                  <p className="text-background">Booking, talent, sponsorship, and press</p>
                </div>
                <div>
                  <p className="text-background/50 uppercase tracking-widest text-xs mb-1">Follow Us</p>
                  <div className="flex items-center gap-3 mt-1">
                    <a href="https://facebook.com/Piedmontwrestlingclub" target="_blank" rel="noopener noreferrer" className="text-[#B09030] hover:text-background transition-colors" data-testid="contact-facebook">Facebook</a>
                    <span className="text-background/20">|</span>
                    <a href="https://instagram.com/Piedmontwrestlingclub" target="_blank" rel="noopener noreferrer" className="text-[#B09030] hover:text-background transition-colors" data-testid="contact-instagram">Instagram</a>
                  </div>
                  <p className="text-background/40 text-xs mt-1">@Piedmontwrestlingclub</p>
                </div>
                <div className="pt-4 border-t border-background/20">
                  <p className="text-background/70 text-xs leading-relaxed">
                    All inquiries will be reviewed by the front office.
                    Response times may vary during show weeks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
